import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Vob, VobClassificationsItem } from '@finpay-estimation/estimation-types';
import { loadSelectedVob, saveSelectedVob, createSelectedVob, loadPatientVobList, loadVobClassifications } from './vob-thunk';

export interface VobState {
  selectedVob: Vob | null;
  patientVobList: Vob[];
  vobClassifications: VobClassificationsItem[];
  isLoading: {
    selectedVob: boolean;
    patientVobList: boolean;
    vobClassifications: boolean;
  };
  error: {
    selectedVob: string | null;
    patientVobList: string | null;
    vobClassifications: string | null;
  };
}

const initialState: VobState = {
  selectedVob: null,
  patientVobList: [],
  vobClassifications: [],
  isLoading: {
    selectedVob: false,
    patientVobList: false,
    vobClassifications: false,
  },
  error: {
    selectedVob: null,
    patientVobList: null,
    vobClassifications: null,
  },
};

const vobSlice = createSlice({
  name: 'vobContext',
  initialState,
  reducers: {
    // Selected VOB actions
    setSelectedVob: (state, action: PayloadAction<Vob | null>) => {
      state.selectedVob = action.payload;
      state.error.selectedVob = null;
    },
    clearSelectedVob: (state) => {
      state.selectedVob = null;
      state.error.selectedVob = null;
    },
    setSelectedVobLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading.selectedVob = action.payload;
    },
    setSelectedVobError: (state, action: PayloadAction<string | null>) => {
      state.error.selectedVob = action.payload;
      state.isLoading.selectedVob = false;
    },
    clearSelectedVobError: (state) => {
      state.error.selectedVob = null;
    },
    
    // Patient VOB List actions
    setPatientVobList: (state, action: PayloadAction<Vob[]>) => {
      state.patientVobList = action.payload;
      state.error.patientVobList = null;
    },
    clearPatientVobList: (state) => {
      state.patientVobList = [];
      state.error.patientVobList = null;
    },
    setPatientVobListLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading.patientVobList = action.payload;
    },
    setPatientVobListError: (state, action: PayloadAction<string | null>) => {
      state.error.patientVobList = action.payload;
      state.isLoading.patientVobList = false;
    },
    clearPatientVobListError: (state) => {
      state.error.patientVobList = null;
    },
    
    // VOB Classifications actions
    setVobClassifications: (state, action: PayloadAction<VobClassificationsItem[]>) => {
      state.vobClassifications = action.payload;
      state.error.vobClassifications = null;
    },
    clearVobClassifications: (state) => {
      state.vobClassifications = [];
      state.error.vobClassifications = null;
    },
    setVobClassificationsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading.vobClassifications = action.payload;
    },
    setVobClassificationsError: (state, action: PayloadAction<string | null>) => {
      state.error.vobClassifications = action.payload;
      state.isLoading.vobClassifications = false;
    },
    clearVobClassificationsError: (state) => {
      state.error.vobClassifications = null;
    },
  },
  extraReducers: (builder) => {
    // Load selected VOB
    builder.addCase(loadSelectedVob.pending, (state) => {
      state.isLoading.selectedVob = true;
      state.error.selectedVob = null;
    });
    builder.addCase(loadSelectedVob.fulfilled, (state, action) => {
      state.isLoading.selectedVob = false;
      state.selectedVob = action.payload;
      state.error.selectedVob = null;
    });
    builder.addCase(loadSelectedVob.rejected, (state, action) => {
      state.isLoading.selectedVob = false;
      state.error.selectedVob = action.error.message || 'Failed to load VOB';
    });

    // Save selected VOB
    builder.addCase(saveSelectedVob.pending, (state) => {
      state.isLoading.selectedVob = true;
      state.error.selectedVob = null;
    });
    builder.addCase(saveSelectedVob.fulfilled, (state, action) => {
      state.isLoading.selectedVob = false;
      state.selectedVob = action.payload;
      state.error.selectedVob = null;
    });
    builder.addCase(saveSelectedVob.rejected, (state, action) => {
      state.isLoading.selectedVob = false;
      state.error.selectedVob = action.error.message || 'Failed to save VOB';
    });

    // Create selected VOB
    builder.addCase(createSelectedVob.pending, (state) => {
      state.isLoading.selectedVob = true;
      state.error.selectedVob = null;
    });
    builder.addCase(createSelectedVob.fulfilled, (state, action) => {
      state.isLoading.selectedVob = false;
      state.selectedVob = action.payload;
      state.error.selectedVob = null;
    });
    builder.addCase(createSelectedVob.rejected, (state, action) => {
      state.isLoading.selectedVob = false;
      state.error.selectedVob = action.error.message || 'Failed to create VOB';
    });

    // Load patient VOB list
    builder.addCase(loadPatientVobList.pending, (state) => {
      state.isLoading.patientVobList = true;
      state.error.patientVobList = null;
    });
    builder.addCase(loadPatientVobList.fulfilled, (state, action) => {
      state.isLoading.patientVobList = false;
      state.patientVobList = action.payload;
      state.error.patientVobList = null;
    });
    builder.addCase(loadPatientVobList.rejected, (state, action) => {
      state.isLoading.patientVobList = false;
      state.error.patientVobList = action.error.message || 'Failed to load patient VOBs';
    });

    // Load VOB classifications
    builder.addCase(loadVobClassifications.pending, (state) => {
      state.isLoading.vobClassifications = true;
      state.error.vobClassifications = null;
    });
    builder.addCase(loadVobClassifications.fulfilled, (state, action) => {
      state.isLoading.vobClassifications = false;
      state.vobClassifications = action.payload;
      state.error.vobClassifications = null;
    });
    builder.addCase(loadVobClassifications.rejected, (state, action) => {
      state.isLoading.vobClassifications = false;
      state.error.vobClassifications = action.error.message || 'Failed to load VOB classifications';
    });
  },
});

export const {
  setSelectedVob,
  clearSelectedVob,
  setSelectedVobLoading,
  setSelectedVobError,
  clearSelectedVobError,
  setPatientVobList,
  clearPatientVobList,
  setPatientVobListLoading,
  setPatientVobListError,
  clearPatientVobListError,
  setVobClassifications,
  clearVobClassifications,
  setVobClassificationsLoading,
  setVobClassificationsError,
  clearVobClassificationsError,
} = vobSlice.actions;

export default vobSlice.reducer; 